/**
 * InfiniteWorld - A DOM-based infinite canvas implementation
 * Supports zoom, pan, add elements, and re-centering
 */

import { CanvasNode } from "../components/canvas/canvas-models";

export interface WorldElement {
  id: string;
  element: HTMLElement;
  x: number;
  y: number;
  width: number;
  height: number;
  parentId?: string;
  contentElement?: HTMLElement;
  footerElement?: HTMLElement;
  canvasElement?: CanvasNode; // CanvasElement from canvas-models
}

export interface WorldTransform {
  x: number;
  y: number;
  scale: number;
}

export interface Connection {
  fromId: string;
  toId: string;
}

export class InfiniteWorld {
  private container: HTMLElement | null = null;
  private worldContainer: HTMLElement | null = null;
  private gridCanvas: HTMLCanvasElement | null = null;
  private arrowsSvg: SVGSVGElement | null = null;
  private elementsContainer: HTMLElement | null = null;
  private contextMenu: HTMLElement | null = null;
  private contextMenuTargetId: string | null = null;

  private transform: WorldTransform = {
    x: 0,
    y: 0,
    scale: 1,
  };

  private elements: Map<string, WorldElement> = new Map();
  private connections: Connection[] = [];
  private selectedElementId: string | null = null;
  private isDragging = false;
  private dragStart = { x: 0, y: 0 };
  private lastPanPosition = { x: 0, y: 0 };

  // Element dragging state
  private isDraggingElement = false;
  private draggedElementId: string | null = null;
  private elementDragStart = { x: 0, y: 0 };
  private elementDragOffset = { x: 0, y: 0 };

  private readonly MIN_ZOOM = 0.1;
  private readonly MAX_ZOOM = 5;
  private readonly ZOOM_SENSITIVITY = 0.001;
  private readonly GRID_SIZE = 20;

  constructor(private handleResurrect: (dashboardId: string) => void) {}

  /**
   * Attach the infinite world to a container div
   */
  public attach(container: HTMLElement): void {
    if (this.container) {
      this.detach();
    }

    this.container = container;
    this.setupWorld();
    this.attachEventListeners();
    this.render();
  }

  /**
   * Detach and clean up the world
   */
  public detach(): void {
    if (!this.container) return;

    this.removeEventListeners();
    this.container.innerHTML = "";
    this.container = null;
    this.worldContainer = null;
    this.gridCanvas = null;
    this.arrowsSvg = null;
    this.elementsContainer = null;
    this.contextMenu = null;
    this.contextMenuTargetId = null;
    this.elements.clear();
    this.connections = [];
    this.selectedElementId = null;
    this.isDraggingElement = false;
    this.draggedElementId = null;
  }

  /**
   * Add an element to the world at specified position
   */
  public addElement(
    element: HTMLElement,
    x: number,
    y: number,
    width: number = 100,
    height: number = 100,
    parentId?: string,
    canvasElement?: any,
  ): string {
    const id = `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Style the element for absolute positioning
    element.style.position = "absolute";
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
    element.style.width = `${width}px`;
    element.style.minHeight = `${height}px`;
    element.style.height = "auto";
    element.style.boxSizing = "border-box";
    element.dataset["worldElementId"] = id;
    element.style.cursor = "pointer";
    element.style.transition = "box-shadow 0.2s ease, border-color 0.2s ease";
    element.style.userSelect = "none";
    element.style.border = "2px solid #4a90e2";
    element.style.borderRadius = "4px";
    element.style.overflow = "hidden";
    element.style.backgroundColor = "#ffffff";
    element.style.display = "flex";
    element.style.flexDirection = "column";

    // Add click handler for selection
    element.addEventListener("click", (e) => {
      if (!this.isDraggingElement) {
        e.stopPropagation();
        this.selectElement(id);
      }
    });

    // Add mousedown handler for dragging
    element.addEventListener("mousedown", (e) => {
      this.onElementMouseDown(e, id);
    });

    const worldElement: WorldElement = {
      id,
      element,
      x,
      y,
      width,
      height,
      parentId,
      canvasElement,
    };

    this.elements.set(id, worldElement);

    // Create connection if parent exists
    if (parentId && this.elements.has(parentId)) {
      this.connections.push({ fromId: parentId, toId: id });
    }

    if (this.elementsContainer) {
      this.elementsContainer.appendChild(element);
    }

    this.render();

    return id;
  }

  /**
   * Create a structured element with header, content, and footer
   */
  public createCanvasElement(node: CanvasNode): HTMLElement {
    const container = document.createElement("div");

    // Header (10% height)
    const header = document.createElement("div");
    header.style.height = "10%";
    header.style.backgroundColor = "#4a90e2";
    header.style.color = "#ffffff";
    header.style.display = "flex";
    header.style.alignItems = "center";
    header.style.justifyContent = "space-between";
    header.style.padding = "0 12px";
    header.style.fontSize = "13px";
    header.style.fontWeight = "600";
    header.style.borderBottom = "1px solid #3a7bc8";

    const headerText = document.createElement("span");
    headerText.textContent = "Actions";
    header.appendChild(headerText);

    const menuButton = document.createElement("button");
    menuButton.textContent = "⋯";
    menuButton.style.background = "none";
    menuButton.style.border = "none";
    menuButton.style.color = "#ffffff";
    menuButton.style.fontSize = "20px";
    menuButton.style.cursor = "pointer";
    menuButton.style.padding = "0 4px";
    menuButton.style.lineHeight = "1";
    menuButton.style.fontWeight = "bold";
    menuButton.style.transition = "background-color 0.2s ease";
    menuButton.style.borderRadius = "3px";

    menuButton.addEventListener("mouseenter", () => {
      menuButton.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
    });
    menuButton.addEventListener("mouseleave", () => {
      menuButton.style.backgroundColor = "";
    });

    // Store reference for later use
    menuButton.dataset["menuButton"] = "true";
    header.appendChild(menuButton);

    container.appendChild(header);

    // Content (fixed minimum height to maintain card size)
    const content = document.createElement("div");
    content.style.minHeight = "200px";
    content.style.height = "200px";
    content.style.display = "flex";
    content.style.alignItems = "center";
    content.style.justifyContent = "center";
    content.style.backgroundColor = "#f9f9f9";
    content.style.overflow = "hidden";
    content.style.borderBottom = "1px solid #e0e0e0";

    if (node.imageUrl) {
      const img = document.createElement("img");
      img.src = node.imageUrl;
      img.style.maxWidth = "100%";
      img.style.maxHeight = "100%";
      img.style.objectFit = "contain";
      img.style.pointerEvents = "none";
      content.appendChild(img);
    } else {
      // Placeholder if no image
      const placeholder = document.createElement("div");
      placeholder.textContent = "🖼️";
      placeholder.style.fontSize = "48px";
      placeholder.style.opacity = "0.3";
      content.appendChild(placeholder);
    }

    container.appendChild(content);

    // Footer (auto height - expands to fit content)
    const footer = document.createElement("div");
    footer.style.height = "auto";
    footer.style.minHeight = "32px";
    footer.style.backgroundColor = "#f5f5f5";
    footer.style.display = "flex";
    footer.style.alignItems = "center";
    footer.style.padding = "8px 12px";
    footer.style.fontSize = "11px";
    footer.style.color = "#666";
    footer.style.lineHeight = "1.4";
    footer.style.wordWrap = "break-word";
    footer.textContent = node.footer || "No description";

    container.appendChild(footer);

    // Store references
    (container as any).__contentElement = content;
    (container as any).__footerElement = footer;

    return container;
  }

  /**
   * Remove an element from the world
   */
  public removeElement(id: string): boolean {
    const worldElement = this.elements.get(id);
    if (!worldElement) return false;

    worldElement.element.remove();
    this.elements.delete(id);

    // Remove connections involving this element
    this.connections = this.connections.filter(
      (conn) => conn.fromId !== id && conn.toId !== id,
    );

    // Deselect if this was selected
    if (this.selectedElementId === id) {
      this.selectedElementId = null;
    }

    this.render();
    return true;
  }

  /**
   * Remove an element and all its descendants
   */
  public removeElementWithChildren(id: string): boolean {
    const descendants = this.getDescendants(id);

    // Remove all descendants first (bottom-up)
    descendants.reverse().forEach((descendantId) => {
      this.removeElement(descendantId);
    });

    // Remove the element itself
    return this.removeElement(id);
  }

  /**
   * Get all descendant IDs of an element (children, grandchildren, etc.)
   */
  private getDescendants(id: string): string[] {
    const descendants: string[] = [];
    const children = this.connections
      .filter((conn) => conn.fromId === id)
      .map((conn) => conn.toId);

    children.forEach((childId) => {
      descendants.push(childId);
      // Recursively get descendants of this child
      descendants.push(...this.getDescendants(childId));
    });

    return descendants;
  }

  /**
   * Get an element by ID
   */
  public getElement(id: string): WorldElement | undefined {
    return this.elements.get(id);
  }

  /**
   * Get all elements
   */
  public getAllElements(): WorldElement[] {
    return Array.from(this.elements.values());
  }

  /**
   * Select an element
   */
  public selectElement(id: string | null): void {
    // Deselect previous
    if (this.selectedElementId) {
      const prevElement = this.elements.get(this.selectedElementId);
      if (prevElement) {
        prevElement.element.style.boxShadow = "";
        prevElement.element.style.borderColor = "#4a90e2";
        prevElement.element.style.borderWidth = "2px";
      }
    }

    this.selectedElementId = id;

    // Apply selection styling
    if (id) {
      const element = this.elements.get(id);
      if (element) {
        element.element.style.boxShadow = "0 0 0 3px rgba(74, 144, 226, 0.3)";
        element.element.style.borderColor = "#1e88e5";
        element.element.style.borderWidth = "3px";
      }
    }
  }

  /**
   * Get the currently selected element ID
   */
  public getSelectedElementId(): string | null {
    return this.selectedElementId;
  }

  /**
   * Get all connections
   */
  public getConnections(): Connection[] {
    return [...this.connections];
  }

  /**
   * Re-center and scale the world to fit all elements
   */
  public recenter(padding: number = 50): void {
    if (this.elements.size === 0 || !this.container) {
      // No elements, just reset to center
      this.transform = { x: 0, y: 0, scale: 1 };
      this.render();
      return;
    }

    // Calculate bounding box of all elements
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    this.elements.forEach((el) => {
      minX = Math.min(minX, el.x);
      minY = Math.min(minY, el.y);
      maxX = Math.max(maxX, el.x + el.width);
      maxY = Math.max(maxY, el.y + el.height);
    });

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const containerWidth = this.container.clientWidth;
    const containerHeight = this.container.clientHeight;

    // Calculate scale to fit all content with padding
    const scaleX = (containerWidth - padding * 2) / contentWidth;
    const scaleY = (containerHeight - padding * 2) / contentHeight;
    const scale = Math.min(scaleX, scaleY, this.MAX_ZOOM);

    // Calculate center position
    const contentCenterX = minX + contentWidth / 2;
    const contentCenterY = minY + contentHeight / 2;

    this.transform.scale = Math.max(scale, this.MIN_ZOOM);
    this.transform.x =
      containerWidth / 2 - contentCenterX * this.transform.scale;
    this.transform.y =
      containerHeight / 2 - contentCenterY * this.transform.scale;

    this.render();
  }

  /**
   * Set zoom level
   */
  public setZoom(scale: number, centerX?: number, centerY?: number): void {
    if (!this.container) return;

    const oldScale = this.transform.scale;
    const newScale = Math.max(this.MIN_ZOOM, Math.min(this.MAX_ZOOM, scale));

    // Zoom towards a specific point (or center of container)
    const zoomPointX = centerX ?? this.container.clientWidth / 2;
    const zoomPointY = centerY ?? this.container.clientHeight / 2;

    // Adjust pan to zoom towards the point
    this.transform.x =
      zoomPointX - (zoomPointX - this.transform.x) * (newScale / oldScale);
    this.transform.y =
      zoomPointY - (zoomPointY - this.transform.y) * (newScale / oldScale);
    this.transform.scale = newScale;

    this.render();
  }

  /**
   * Set pan position
   */
  public setPan(x: number, y: number): void {
    this.transform.x = x;
    this.transform.y = y;
    this.render();
  }

  /**
   * Get current transform
   */
  public getTransform(): WorldTransform {
    return { ...this.transform };
  }

  /**
   * Setup the world structure
   */
  private setupWorld(): void {
    if (!this.container) return;

    // Clear container
    this.container.innerHTML = "";

    // Set container styles
    this.container.style.position = "relative";
    this.container.style.overflow = "hidden";
    this.container.style.width = "100%";
    this.container.style.height = "100%";
    this.container.style.cursor = "grab";

    // Create grid canvas
    this.gridCanvas = document.createElement("canvas");
    this.gridCanvas.style.position = "absolute";
    this.gridCanvas.style.top = "0";
    this.gridCanvas.style.left = "0";
    this.gridCanvas.style.width = "100%";
    this.gridCanvas.style.height = "100%";
    this.gridCanvas.style.pointerEvents = "none";
    this.container.appendChild(this.gridCanvas);

    // Create SVG layer for arrows
    this.arrowsSvg = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    this.arrowsSvg.style.position = "absolute";
    this.arrowsSvg.style.top = "0";
    this.arrowsSvg.style.left = "0";
    this.arrowsSvg.style.width = "100%";
    this.arrowsSvg.style.height = "100%";
    this.arrowsSvg.style.pointerEvents = "none";
    this.arrowsSvg.style.overflow = "visible";
    this.container.appendChild(this.arrowsSvg);

    // Define arrow marker
    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    const marker = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "marker",
    );
    marker.setAttribute("id", "arrowhead");
    marker.setAttribute("markerWidth", "10");
    marker.setAttribute("markerHeight", "10");
    marker.setAttribute("refX", "9");
    marker.setAttribute("refY", "3");
    marker.setAttribute("orient", "auto");
    const polygon = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "polygon",
    );
    polygon.setAttribute("points", "0 0, 10 3, 0 6");
    polygon.setAttribute("fill", "#4a90e2");
    marker.appendChild(polygon);
    defs.appendChild(marker);
    this.arrowsSvg.appendChild(defs);

    // Create world container (for transforms)
    this.worldContainer = document.createElement("div");
    this.worldContainer.style.position = "absolute";
    this.worldContainer.style.top = "0";
    this.worldContainer.style.left = "0";
    this.worldContainer.style.transformOrigin = "0 0";
    this.worldContainer.style.willChange = "transform";
    this.container.appendChild(this.worldContainer);

    // Create elements container
    this.elementsContainer = document.createElement("div");
    this.elementsContainer.style.position = "relative";
    this.elementsContainer.style.width = "0";
    this.elementsContainer.style.height = "0";
    this.worldContainer.appendChild(this.elementsContainer);

    // Create context menu
    this.createContextMenu();
  }

  /**
   * Create the context menu
   */
  private createContextMenu(): void {
    if (!this.container) return;

    this.contextMenu = document.createElement("div");
    this.contextMenu.style.position = "absolute";
    this.contextMenu.style.display = "none";
    this.contextMenu.style.backgroundColor = "#ffffff";
    this.contextMenu.style.border = "1px solid #d0d0d0";
    this.contextMenu.style.borderRadius = "6px";
    this.contextMenu.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
    this.contextMenu.style.zIndex = "10000";
    this.contextMenu.style.minWidth = "160px";
    this.contextMenu.style.padding = "4px 0";
    this.contextMenu.style.userSelect = "none";

    // Menu items will be populated dynamically in showContextMenu

    this.container.appendChild(this.contextMenu);
  }

  /**
   * Create a context menu item
   */
  private createMenuItem(text: string, onClick: () => void): HTMLElement {
    const item = document.createElement("div");
    item.textContent = text;
    item.style.padding = "8px 16px";
    item.style.cursor = "pointer";
    item.style.fontSize = "14px";
    item.style.transition = "background-color 0.15s ease";

    item.addEventListener("mouseenter", () => {
      item.style.backgroundColor = "#f5f5f5";
    });

    item.addEventListener("mouseleave", () => {
      item.style.backgroundColor = "";
    });

    item.addEventListener("click", (e) => {
      e.stopPropagation();
      onClick();
      this.hideContextMenu();
    });

    return item;
  }

  /**
   * Show context menu at position
   */
  private showContextMenu(elementId: string, x: number, y: number): void {
    if (!this.contextMenu || !this.container) return;

    this.contextMenuTargetId = elementId;

    // Clear existing menu items
    this.contextMenu.innerHTML = "";

    const worldElement = this.elements.get(elementId);
    if (!worldElement) return;

    // Add dynamic menu items from canvasElement.context.items
    if (worldElement.canvasElement?.context?.items) {
      worldElement.canvasElement.context.items.forEach((item: any) => {
        const menuItem = this.createMenuItem(item.label, item.action);
        this.contextMenu!.appendChild(menuItem);
      });
    }

    // Add separator if there are custom items
    if (
      worldElement.canvasElement?.context?.items &&
      worldElement.canvasElement.context.items.length > 0
    ) {
      const separator = document.createElement("div");
      separator.style.height = "1px";
      separator.style.backgroundColor = "#e0e0e0";
      separator.style.margin = "4px 0";
      this.contextMenu.appendChild(separator);
    }

    // Always add the delete option at the bottom
    const deleteItem = this.createMenuItem("🗑️ Delete (with children)", () =>
      this.onContextMenuDelete(),
    );
    const resurrectItem = this.createMenuItem("Resurrect", () => {
      const dashboardId = worldElement.canvasElement?.dashboardId;
      if(!dashboardId) {
        console.error("No dashboard ID found for resurrection.");
        return;
      }
      this.handleResurrect(dashboardId);
    });
    deleteItem.style.color = "#d32f2f";
    this.contextMenu.appendChild(resurrectItem);
    this.contextMenu.appendChild(deleteItem);

    // Position the menu
    this.contextMenu.style.left = `${x}px`;
    this.contextMenu.style.top = `${y}px`;
    this.contextMenu.style.display = "block";

    // Adjust position if menu goes off screen
    const rect = this.contextMenu.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();

    if (rect.right > containerRect.right) {
      this.contextMenu.style.left = `${x - rect.width}px`;
    }
    if (rect.bottom > containerRect.bottom) {
      this.contextMenu.style.top = `${y - rect.height}px`;
    }
  }

  /**
   * Hide context menu
   */
  private hideContextMenu(): void {
    if (!this.contextMenu) return;
    this.contextMenu.style.display = "none";
    this.contextMenuTargetId = null;
  }

  /**
   * Handle context menu "Delete" action
   */
  private onContextMenuDelete(): void {
    if (!this.contextMenuTargetId) return;
    this.removeElementWithChildren(this.contextMenuTargetId);
  }

  /**
   * Generate a random pastel color
   */
  private getRandomPastelColor(): string {
    const colors = [
      "#ffebee",
      "#e8f5e9",
      "#e3f2fd",
      "#f3e5f5",
      "#fff3e0",
      "#fce4ec",
      "#e0f2f1",
      "#f9fbe7",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  /**
   * Render the world (apply transforms and draw grid)
   */
  private render(): void {
    this.updateTransform();
    this.drawGrid();
    this.drawArrows();
  }

  /**
   * Update transform on world container
   */
  private updateTransform(): void {
    if (!this.worldContainer) return;

    this.worldContainer.style.transform = `translate(${this.transform.x}px, ${this.transform.y}px) scale(${this.transform.scale})`;
  }

  /**
   * Draw the grid background
   */
  private drawGrid(): void {
    if (!this.gridCanvas || !this.container) return;

    const canvas = this.gridCanvas;
    const rect = this.container.getBoundingClientRect();

    // Set canvas size to match container
    canvas.width = rect.width;
    canvas.height = rect.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate grid parameters
    const gridSize = this.GRID_SIZE * this.transform.scale;
    const offsetX = this.transform.x % gridSize;
    const offsetY = this.transform.y % gridSize;

    // Draw grid lines
    ctx.strokeStyle = "#e0e0e0";
    ctx.lineWidth = 1;
    ctx.beginPath();

    // Vertical lines
    for (let x = offsetX; x < canvas.width; x += gridSize) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
    }

    // Horizontal lines
    for (let y = offsetY; y < canvas.height; y += gridSize) {
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
    }

    ctx.stroke();

    // Draw origin axes (optional)
    const originX = this.transform.x;
    const originY = this.transform.y;

    if (originX >= 0 && originX <= canvas.width) {
      ctx.strokeStyle = "#ff000040";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(originX, 0);
      ctx.lineTo(originX, canvas.height);
      ctx.stroke();
    }

    if (originY >= 0 && originY <= canvas.height) {
      ctx.strokeStyle = "#00ff0040";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, originY);
      ctx.lineTo(canvas.width, originY);
      ctx.stroke();
    }
  }

  /**
   * Draw arrows between connected elements
   */
  private drawArrows(): void {
    if (!this.arrowsSvg || this.connections.length === 0) return;

    const svg = this.arrowsSvg;

    // Clear existing arrows (except defs)
    const defs = svg.querySelector("defs");
    svg.innerHTML = "";
    if (defs) {
      svg.appendChild(defs);
    }

    // Draw each connection
    this.connections.forEach((conn) => {
      const fromElement = this.elements.get(conn.fromId);
      const toElement = this.elements.get(conn.toId);

      if (!fromElement || !toElement) return;

      // Calculate center points of elements
      const fromX = fromElement.x + fromElement.width / 2;
      const fromY = fromElement.y + fromElement.height / 2;
      const toX = toElement.x + toElement.width / 2;
      const toY = toElement.y + toElement.height / 2;

      // Apply world transform to coordinates
      const screenFromX = fromX * this.transform.scale + this.transform.x;
      const screenFromY = fromY * this.transform.scale + this.transform.y;
      const screenToX = toX * this.transform.scale + this.transform.x;
      const screenToY = toY * this.transform.scale + this.transform.y;

      // Calculate arrow endpoint (touching the edge of the target element)
      const dx = toX - fromX;
      const dy = toY - fromY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance === 0) return;

      const unitX = dx / distance;
      const unitY = dy / distance;

      // Offset by half the element dimensions to stop at the edge
      const offsetDistance = Math.min(
        Math.abs(toElement.width / 2 / unitX),
        Math.abs(toElement.height / 2 / unitY),
      );

      const endX =
        (toX - unitX * offsetDistance) * this.transform.scale +
        this.transform.x;
      const endY =
        (toY - unitY * offsetDistance) * this.transform.scale +
        this.transform.y;

      // Create path
      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttribute(
        "d",
        `M ${screenFromX} ${screenFromY} L ${endX} ${endY}`,
      );
      path.setAttribute("stroke", "#4a90e2");
      path.setAttribute(
        "stroke-width",
        String(2 * Math.max(0.5, this.transform.scale)),
      );
      path.setAttribute("fill", "none");
      path.setAttribute("marker-end", "url(#arrowhead)");

      svg.appendChild(path);
    });
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    if (!this.container) return;

    this.container.addEventListener("wheel", this.onWheel);
    this.container.addEventListener("mousedown", this.onMouseDown);
    this.container.addEventListener("mousemove", this.onMouseMove);
    this.container.addEventListener("mouseup", this.onMouseUp);
    this.container.addEventListener("mouseleave", this.onMouseLeave);
    this.container.addEventListener("click", this.onContainerClick);
    this.container.addEventListener("contextmenu", this.onContainerContextMenu);
  }

  /**
   * Remove event listeners
   */
  private removeEventListeners(): void {
    if (!this.container) return;

    this.container.removeEventListener("wheel", this.onWheel);
    this.container.removeEventListener("mousedown", this.onMouseDown);
    this.container.removeEventListener("mousemove", this.onMouseMove);
    this.container.removeEventListener("mouseup", this.onMouseUp);
    this.container.removeEventListener("mouseleave", this.onMouseLeave);
    this.container.removeEventListener("click", this.onContainerClick);
    this.container.removeEventListener(
      "contextmenu",
      this.onContainerContextMenu,
    );
  }

  /**
   * Handle mouse wheel (zoom)
   */
  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();

    if (!this.container) return;

    const rect = this.container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate zoom delta
    const delta = -e.deltaY * this.ZOOM_SENSITIVITY;
    const newScale = this.transform.scale * (1 + delta);

    this.setZoom(newScale, mouseX, mouseY);
  };

  /**
   * Handle mouse down (start pan)
   */
  private onMouseDown = (e: MouseEvent): void => {
    // Only pan with left mouse button or middle button
    if (e.button !== 0 && e.button !== 1) return;

    // Don't pan if clicking on an element (allow element interaction)
    const target = e.target as HTMLElement;
    if (target.dataset["worldElementId"]) return;

    // Don't start panning if dragging an element
    if (this.isDraggingElement) return;

    this.isDragging = true;
    this.dragStart = { x: e.clientX, y: e.clientY };
    this.lastPanPosition = { x: this.transform.x, y: this.transform.y };

    if (this.container) {
      this.container.style.cursor = "grabbing";
    }

    e.preventDefault();
  };

  /**
   * Handle element mouse down (start element drag)
   */
  private onElementMouseDown = (e: MouseEvent, elementId: string): void => {
    // Only drag with left mouse button
    if (e.button !== 0) return;

    // Check if clicking on menu button
    const target = e.target as HTMLElement;
    if (target.dataset["menuButton"] === "true") {
      e.stopPropagation();
      e.preventDefault();
      // Open context menu at button position (convert from viewport to container coords)
      if (this.container) {
        const rect = target.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();
        this.showContextMenu(
          elementId,
          rect.left - containerRect.left,
          rect.bottom - containerRect.top + 5,
        );
      }
      return;
    }

    const element = this.elements.get(elementId);
    if (!element) return;

    e.stopPropagation();
    e.preventDefault();

    this.isDraggingElement = true;
    this.draggedElementId = elementId;

    // Store the mouse position in world coordinates
    const worldMouseX = (e.clientX - this.transform.x) / this.transform.scale;
    const worldMouseY = (e.clientY - this.transform.y) / this.transform.scale;

    // Store offset from element's top-left corner
    this.elementDragOffset = {
      x: worldMouseX - element.x,
      y: worldMouseY - element.y,
    };

    this.elementDragStart = { x: e.clientX, y: e.clientY };

    // Change cursor and add drag styling
    element.element.style.cursor = "grabbing";
    element.element.style.boxShadow = "0 8px 16px rgba(0, 0, 0, 0.2)";
    element.element.style.opacity = "0.8";
    element.element.style.zIndex = "1000";
  };

  /**
   * Handle mouse move (pan or drag element)
   */
  private onMouseMove = (e: MouseEvent): void => {
    // Handle element dragging
    if (this.isDraggingElement && this.draggedElementId) {
      const element = this.elements.get(this.draggedElementId);
      if (!element) return;

      // Calculate new position in world coordinates
      const worldMouseX = (e.clientX - this.transform.x) / this.transform.scale;
      const worldMouseY = (e.clientY - this.transform.y) / this.transform.scale;

      // Update element position (accounting for drag offset)
      element.x = worldMouseX - this.elementDragOffset.x;
      element.y = worldMouseY - this.elementDragOffset.y;

      // Update DOM element position
      element.element.style.left = `${element.x}px`;
      element.element.style.top = `${element.y}px`;

      // Redraw arrows
      this.drawArrows();

      return;
    }

    // Handle canvas panning
    if (!this.isDragging) return;

    const dx = e.clientX - this.dragStart.x;
    const dy = e.clientY - this.dragStart.y;

    this.transform.x = this.lastPanPosition.x + dx;
    this.transform.y = this.lastPanPosition.y + dy;

    this.render();
  };

  /**
   * Handle mouse up (end pan or element drag)
   */
  private onMouseUp = (): void => {
    // Handle element drag end
    if (this.isDraggingElement && this.draggedElementId) {
      const element = this.elements.get(this.draggedElementId);
      if (element) {
        element.element.style.cursor = "pointer";
        element.element.style.opacity = "1";
        element.element.style.zIndex = "";

        // Restore selection styling if this element is selected
        if (this.selectedElementId === this.draggedElementId) {
          element.element.style.boxShadow = "0 0 0 3px rgba(74, 144, 226, 0.3)";
        } else {
          element.element.style.boxShadow = "";
        }
      }
      this.isDraggingElement = false;
      this.draggedElementId = null;
    }

    // Handle pan end
    if (!this.isDragging) return;

    this.isDragging = false;

    if (this.container) {
      this.container.style.cursor = "grab";
    }
  };

  /**
   * Handle mouse leave (end pan)
   */
  private onMouseLeave = (): void => {
    this.onMouseUp();
  };

  /**
   * Handle container click (deselect when clicking empty space)
   */
  private onContainerClick = (e: MouseEvent): void => {
    // Don't trigger if we just finished dragging an element
    if (this.isDraggingElement) return;

    // Hide context menu when clicking anywhere
    this.hideContextMenu();

    const target = e.target as HTMLElement;
    // If clicking on the container itself (not an element), deselect
    if (!target.dataset["worldElementId"]) {
      this.selectElement(null);
    }
  };

  /**
   * Handle container right-click (prevent default context menu)
   */
  private onContainerContextMenu = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;
    // Only prevent default if not on an element (elements handle their own context menu)
    if (!target.dataset["worldElementId"]) {
      e.preventDefault();
      this.hideContextMenu();
    }
  };
}
